import {SearchParameters} from '@findhotel/sapi'
import {omitSeoSortField} from 'modules/search/sortField'
import {
  FilterUrlParam,
  FilterUrlParams,
  SortField,
  SortOrder
} from 'modules/search/types'
import {isNil, path} from 'ramda'

import {Lov} from '@vio/core/src/analytics/types/Events'

type ListOfValuesType = Lov | null | undefined

/**
 * Toggle filter
 */
export const toggleFilter = (
  searchParam: string,
  value: string | null | undefined
): string | null | undefined => {
  return searchParam !== value ? value : undefined
}

/**
 * Toggle multi filter (Array)
 */
export const toggleMultiFilter = (
  searchParam: string | number | string[] | number[],
  value: string | null | undefined
) => {
  // TODO: TS - missing type annotations for S and T
  const searchParamToUpdate = searchParam ? [].concat(searchParam) : []
  const index = searchParamToUpdate.indexOf(value)

  if (index === -1) {
    searchParamToUpdate.push(value)
  } else {
    searchParamToUpdate.splice(index, 1)
  }

  return searchParamToUpdate
}

/**
 * Force set multi filter (Array)
 */
export const forceSetMultiFilters = (
  searchParam: string | number | (string | number)[],
  values: (string | number)[]
): (string | number)[] => {
  if (Array.isArray(searchParam)) {
    const appliedParams = new Set<number | string>(searchParam)
    values.forEach(value => appliedParams.add(value))
    return [...appliedParams].filter(i => !isNil(i))
  } else {
    return [searchParam, ...values]
  }
}

/**
 * Force remove multi filter (Array)
 */
export const forceRemoveMultiFilters = (
  searchParam: string | number | (string | number)[],
  values: (string | number)[]
): (string | number)[] => {
  if (Array.isArray(searchParam)) {
    const appliedParams = new Set<number | string>(searchParam)
    values.forEach(element => appliedParams.delete(element))
    return [...appliedParams].filter(i => !isNil(i))
  } else {
    const singleParams = new Set<number | string>([searchParam])
    values.forEach(element => singleParams.delete(element))
    return [...singleParams].filter(i => !isNil(i))
  }
}

export const getFacetLabel =
  (type: string, listOfValues: ListOfValuesType) =>
  (facetId: string): string =>
    path([`${type}:${facetId}`, 'value'], listOfValues)

/**
 * Factory function that checks whether the facet is selected or not
 */

export const isFacetSelected = (
  filters: FilterUrlParam,
  facetId: string
): boolean => {
  if (!filters) return false
  if (Array.isArray(filters)) return filters.includes(facetId)
  return filters.toString() === facetId
}

/**
 * Converts a boolean value to URL parameter format
 * @param value - Boolean value to convert
 * @returns '1' for true, '0' for false
 */
export const castBooleanToString = (
  value: boolean | undefined
): BooleanUrlParam => (isNil(value) ? undefined : value ? '1' : '0')

/**
 * Converts an array of numbers to an array of strings
 * @param value - Number array to convert
 * @returns String array or undefined
 */
export const castNumberArrayToString = (
  value: number[] | undefined
): string[] | undefined => (value?.length ? value.map(String) : undefined)

/**
 * Extracts and transforms filter parameters from SAPI search parameters to URL-compatible format
 * @param searchParams - SAPI search parameters object
 * @returns Transformed filter parameters suitable for URL params
 */
export const transformSapiFiltersToFiltersURL = (
  searchParams: SearchParameters
): FilterUrlParams => {
  const {filters, sortField, sortOrder} = searchParams

  // We don't allow multiple guest ratings, so we only take the first one
  const reviewScore = filters?.guestRating?.length
    ? String(filters?.guestRating[0])
    : undefined

  return {
    freeCancellation: castBooleanToString(filters?.freeCancellation),
    payLater: castBooleanToString(filters?.payLater),
    exclusive: castBooleanToString(filters?.exclusive),
    facilities: castNumberArrayToString(filters?.facilities),
    propertyTypes: castNumberArrayToString(filters?.propertyTypeId),
    starRatings: castNumberArrayToString(filters?.starRating),
    themes: castNumberArrayToString(filters?.themeIds),
    hotelName: filters?.hotelName,
    priceMin: filters?.priceMin,
    priceMax: filters?.priceMax,
    amenities: filters?.amenities,
    brandIds: filters?.brandIds,
    neighborhoodIds: filters?.neighborhoodIds,
    reviewScore,
    sortField: omitSeoSortField(sortField as SortField),
    sortOrder: sortOrder as SortOrder,
    dealFreeze: castBooleanToString(filters?.dealFreeze) // Added as a part of c747927f-deal-freeze-srp
  }
}
